import React, { useState, useRef, useEffect, MouseEvent } from 'react';
import './App.css';
import Header from "./components/Header/Header";
import SettingCenter from "./components/SettingCenter/SettingCenter";
import ImageViewer from "./components/ImageViewer/ImageViewer";
import LogViewer from "./components/LogViewer/LogViewer";
import MobileBtns from "./components/MobileBtns/MobileBtns";
import Mask from "./components/Mask/Mask";
import Faq from "./components/Faq/Faq";
import Footer from "./components/Footer/Footer";
import CameraVideo from './components/CameraVideo/CameraVideo';
import Redirect from './components/Redirect/Redirect';
import Dialog from "./components/Dialog/Dialog";
import { device } from "./utils/helpers";
import { useDispatch, useSelector } from 'react-redux';
import { PreArrRef } from './utils/typesAndMaps';
import { RootStore } from './store';
import { updateDialogState } from './store/features/dialogStatesSlice';
import { updateStyles } from './store/features/nodeStylesSlice';

export default function App() {
  const logPreRef: PreArrRef = useRef([]);
  const clickSampleImage = useRef(false);
  const responseCount = useRef(false);

  const [isShowSettings, setSettingsVisibility] = useState(false);
  const [isShowImagePanel, setImagePanelVisibility] = useState(false);
  const [isShowRedirectModal, setRedirectModalVisibility] = useState(false);

  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const response = useSelector((store: RootStore) => store.decodeResponse);
  const imagesData = useSelector((store: RootStore) => store.imagesData);
  const dialogStates = useSelector((store: RootStore) => store.dialogStates);
  const nodeStyles = useSelector((store: RootStore) => store.nodeStyles);

  const generalStatesRef = useRef(generalStates);
  const imageDataRef = useRef(imagesData);
  const nodeStylesRef = useRef(nodeStyles);

  const dispatch = useDispatch();

  useEffect(() => {
    generalStatesRef.current = generalStates;
    imageDataRef.current = imagesData;
    nodeStylesRef.current = nodeStyles;
  }, [generalStates, imagesData, nodeStyles]);

  useEffect(() => {
    dispatch(updateStyles({
      settingsCenterStyles: {
        ...nodeStylesRef.current.settingsCenterStyles,
        display: (generalStates.settingsToggle || device() === "mobile") ? "none" : "block"
      }
    }));
  }, [dispatch, generalStates.settingsToggle]);

  useEffect(() => {
    window.addEventListener("resize", () => {
      const deviceType = device();
      if (["ipad", "pc"].includes(deviceType)) {
        dispatch(updateStyles({
          settingsCenterStyles: {
            ...nodeStylesRef.current.settingsCenterStyles,
            position: "relative", display: deviceType === "pc" && generalStatesRef.current.settingsToggle ? "none" : "block"
          },
          mobileBtnsStyles: {
            ...nodeStylesRef.current.mobileBtnsStyles,
            display: "none"
          },
          imageListWrapperStyles: {
            ...nodeStylesRef.current.imageListWrapperStyles,
            display: "block"
          }
        }));
        setSettingsVisibility(true);
        setImagePanelVisibility(true);
      } else {
        dispatch(updateStyles({
          settingsCenterStyles: {
            ...nodeStylesRef.current.settingsCenterStyles,
            display: "none"
          },
          mobileBtnsStyles: {
            ...nodeStylesRef.current.mobileBtnsStyles,
            display: "flex"
          },
          imageListWrapperStyles: {
            ...nodeStylesRef.current.imageListWrapperStyles,
            display: "none"
          }
        }));

        setSettingsVisibility(false);
        setImagePanelVisibility(false);
      }
    });

    document.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const inputs = document.querySelectorAll("input");
        inputs.forEach(input => input.blur());
      }
    });

    if ((navigator.userAgent.match(/Android/i) || navigator.userAgent.match(/iPhone|iPad|iPod/i)) && WebAssembly) {
      setRedirectModalVisibility(true);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!clickSampleImage.current && generalStates.curImgIdx === 1) {
      dispatch(updateDialogState({ stateName: "isShowDialog", value: true }));
      clickSampleImage.current = true;
    }
  }, [generalStates.curImgIdx, dispatch]);

  useEffect(() => {
    if (!responseCount.current && generalStatesRef.current.curImgIdx !== -1 && !imageDataRef.current[generalStatesRef.current.curImgIdx].sampleImg && response.results && response.results.length >= 20) {
      dispatch(updateDialogState({ stateName: "isShowDialog", value: true }));
      responseCount.current = true;
    }
  }, [response, dispatch]);

  const showSettings = () => {
    if (!isShowSettings) {
      dispatch(updateStyles({
        settingsCenterStyles: {
          ...nodeStyles.settingsCenterStyles,
          position: "absolute",
          display: "block"
        },
        mobileBtnsStyles: {
          ...nodeStylesRef.current.mobileBtnsStyles,
          display: "none"
        }
      }));

      setSettingsVisibility(true);
    }
  }

  const showImagePanel = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (nodeStyles.imageListWrapperStyles.display === "none") {
      dispatch(updateStyles({
        imageListWrapperStyles: {
          ...nodeStylesRef.current.imageListWrapperStyles,
          display: "block"
        }
      }));
      setImagePanelVisibility(true);
    }
  }

  const hideSettingsOrPanel = () => {
    if (isShowSettings) {
      dispatch(updateStyles({
        settingsCenterStyles: {
          ...nodeStyles.settingsCenterStyles,
          display: "none"
        },
        mobileBtnsStyles: {
          ...nodeStylesRef.current.mobileBtnsStyles,
          display: "flex"
        }
      }));

      setSettingsVisibility(false);
    } else {
      dispatch(updateStyles({
        imageListWrapperStyles: {
          ...nodeStylesRef.current.imageListWrapperStyles,
          display: "none"
        }
      }));
      setImagePanelVisibility(false);
    }
  };

  return (
    <div className="dbr-container">
      {dialogStates.isShowDialog && <Dialog title={dialogStates.title} content={dialogStates.content} />}
      {isShowRedirectModal && <Redirect setRedirectModalVisibility={setRedirectModalVisibility} />}
      {generalStates.cameraState && <CameraVideo />}
      <Header />
      <MobileBtns
        showSettings={showSettings}
        showImagePanel={showImagePanel}
      />
      {(isShowSettings || isShowImagePanel) && <Mask hideSettingsOrPanel={hideSettingsOrPanel} />}
      <div className="dbr-main">
        <SettingCenter
          hideSettingsOrPanel={hideSettingsOrPanel}
        />
        <ImageViewer logPreRef={logPreRef} />
        <LogViewer logPreRef={logPreRef} />
      </div>
      <Faq />
      <Footer />
    </div>
  );
}